import { createSelector } from 'reselect';
import { RootState } from '../../core/store';
import { IPortfolioVesselForm, IPortfolioForm, IPortfolioWithVesselsForm, ScopeOptions } from './portfolioTypes'
import { remove, orderBy } from 'lodash'
import { selectSubscribedMsiVessels, selectUserId } from '../../user-management/appSelectors';

export const selectSubscribedMsiVesselFormList = createSelector(
    [selectSubscribedMsiVessels], (subscribedMsiVessels) => orderBy(subscribedMsiVessels.map((v) => ({ vesselName: v.vesselName, imoNumber: v.imoNumber, sectorName: v.sectorName, companyName: v.owner })), 'vesselName') as IPortfolioVesselForm[]
);


const selectPortfolioSlice = (state: RootState) => state.portfolioSlice;

export const selectPortfoliosBelongingToUser = createSelector(
    [selectPortfolioSlice],
    (portfolioSlice) => portfolioSlice.portfolios.filter(k => k.scope === ScopeOptions.Personal).map((p) => ({ id: p.id, name: p.name, scope: p.scope, isDraft: p.isDraft, tempId: p.tempId, isOwnPortfolio: true })) as IPortfolioForm[]
);

export const selectPortfoliosSharedWithUser = createSelector(
    [selectPortfolioSlice, selectUserId],
    (portfolioSlice, userId) => portfolioSlice.portfolios.filter(k => k.scope === ScopeOptions.Team).map((p) => ({ id: p.id, name: p.name, scope: p.scope, isDraft: p.isDraft, tempId: p.tempId, isOwnPortfolio: p.createdById === userId })) as IPortfolioForm[]
);



export const selectSelectedPortfolioWithMsiVessels = createSelector(
    [selectPortfolioSlice, selectSubscribedMsiVessels, selectUserId],
    (portfolioSlice, subscribedMsiVessels, userId) => {
        if (!portfolioSlice.selectedPortfolio)
            return {
                id: null,
                name: '',
                scope: '',
                isOwnPortfolio: true,
                portfolioWithMsiVesselsForm: [] as IPortfolioVesselForm[]
            } as IPortfolioWithVesselsForm;

        const tempMsiVesselList: IPortfolioVesselForm[] = portfolioSlice.selectedPortfolio.portfolioVesselsImoList?.reduce((acc, imoNumber) => {
            const vessel = subscribedMsiVessels?.find(vessel => vessel.imoNumber === imoNumber);
            if (vessel) {
                acc.push({ vesselName: vessel.vesselName, imoNumber: vessel.imoNumber, sectorName: vessel.sectorName, companyName: vessel.owner });
            }
            return acc;
        }, [] as IPortfolioVesselForm[]) || [];

        return {
            id: portfolioSlice.selectedPortfolio.id,
            name: portfolioSlice.selectedPortfolio.name,
            scope: portfolioSlice.selectedPortfolio.scope,
            isDraft: portfolioSlice.selectedPortfolio.isDraft,
            tempId: portfolioSlice.selectedPortfolio.tempId,
            isOwnPortfolio: portfolioSlice.selectedPortfolio.createdById === userId ? true : false,
            portfolioWithMsiVesselsForm: tempMsiVesselList
        } as IPortfolioWithVesselsForm;
    }
);

export const selectPortfolioNames = createSelector(
    [selectPortfolioSlice, selectSelectedPortfolioWithMsiVessels],
    // it excludes the portfolio name from the list of portfolio names if the portfolio is selected - draft or update
    (portfolioSlice, selectedPortfolioWithMsiVessels) => selectedPortfolioWithMsiVessels?.id !== null || (selectedPortfolioWithMsiVessels.id === null && selectedPortfolioWithMsiVessels.isDraft === true) ?
        remove(portfolioSlice.portfolios.map(f => f.name), r => r !== selectedPortfolioWithMsiVessels?.name) : portfolioSlice.portfolios.map(f => f.name) as string[]
);

export const selectIsPortfolioLoading = createSelector(
    selectPortfolioSlice,
    (portfolioSlice) => portfolioSlice.isPortfoliosLoading
);

export const selectIsPortfolioWithVesselsLoading = createSelector(
    selectPortfolioSlice,
    (portfolioSlice) => portfolioSlice.isPortfolioVesselsLoading
);

export const selectIsPortfolioBusy = createSelector(
    selectPortfolioSlice,
    (portfolioSlice) => portfolioSlice.isBusy
);

export const selectPortfoliosFetched = createSelector(
    selectPortfolioSlice,
    (portfolioSlice) => portfolioSlice.isFetched
);
